import { FC, useState } from "react"
import { FaPlus, FaTrash } from "react-icons/fa"

import { PrizeDistributionEntry } from "types/tournament.types"

import { formatTournamentMoney, getCurrencySymbol } from "util/formatter"

interface Props {
  value: PrizeDistributionEntry[]
  onChange: (value: PrizeDistributionEntry[]) => void
  prizePoolBase?: string
  forcePercentageOnly?: boolean
  currency?: string
}

export const PrizeDistributionBuilder: FC<Props> = ({ value, onChange, prizePoolBase, forcePercentageOnly, currency }) => {
  const [entries, setEntries] = useState<PrizeDistributionEntry[]>(value.length > 0 ? value : [])

  const addEntry = () => {
    const newEntries = [
      ...entries,
      {
        rank: entries.length + 1,
        percentage: 0
      }
    ]
    setEntries(newEntries)
    onChange(newEntries)
  }

  const removeEntry = (index: number) => {
    const newEntries = entries.filter((_, i) => i !== index)
    const reordered = newEntries.map((entry, i) => ({
      ...entry,
      rank: i + 1
    }))
    setEntries(reordered)
    onChange(reordered)
  }

  const updateEntry = (index: number, field: string, value: any) => {
    const newEntries = [...entries]
    newEntries[index] = {
      ...newEntries[index],
      [field]: value ? parseFloat(value) : undefined
    }
    setEntries(newEntries)
    onChange(newEntries)
  }

  const toggleType = (index: number, type: "percentage" | "amount") => {
    const newEntries = [...entries]
    const current = newEntries[index]
    if (type === "percentage" && current.percentage === undefined) {
      const val = current.amount ?? 0
      delete newEntries[index].amount
      newEntries[index].percentage = val
    } else if (type === "amount" && current.amount === undefined) {
      const val = current.percentage ?? 0
      delete newEntries[index].percentage
      newEntries[index].amount = val
    }
    setEntries(newEntries)
    onChange(newEntries)
  }

  const totalPercentage = entries.reduce((sum, entry) => sum + (entry.percentage || 0), 0)
  const totalAmount = entries.reduce((sum, entry) => sum + (entry.amount || 0), 0)
  const hasPercentages = entries.some((e) => e.percentage !== undefined)
  const hasAmounts = entries.some((e) => e.amount !== undefined)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Prize Distribution</label>
        <button
          type="button"
          onClick={addEntry}
          className="flex items-center gap-2 px-3 py-1 text-sm bg-[rgba(169,78,206,1)] text-white rounded hover:bg-[rgba(169,78,206,0.8)]"
        >
          <FaPlus /> Add Rank
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400">No prize distribution configured</p>
          <button type="button" onClick={addEntry} className="mt-2 text-[rgba(169,78,206,1)] hover:text-[rgba(169,78,206,0.8)]">
            Add your first rank
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((entry, index) => (
            <div key={index} className="flex items-center gap-3 p-4 border border-gray-300 dark:border-gray-700 rounded-lg">
              <div className="flex-shrink-0 w-16">
                <label className="text-xs text-gray-500 dark:text-gray-400">Rank</label>
                <div className="font-bold text-lg text-gray-900 dark:text-white">{entry.rank}</div>
              </div>

              <div className="flex-1 flex items-end gap-3">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 dark:text-gray-400">
                    {entry.percentage !== undefined ? "Percentage (%)" : "Fixed Amount (cents)"}
                  </label>
                  <input
                    type="number"
                    step={entry.percentage !== undefined ? "0.01" : "1"}
                    min="0"
                    max={entry.percentage !== undefined ? "100" : undefined}
                    value={entry.percentage !== undefined ? entry.percentage : entry.amount || ""}
                    onChange={(e) => updateEntry(index, entry.percentage !== undefined ? "percentage" : "amount", e.target.value)}
                    className="w-full px-3 py-2 border rounded dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    placeholder={entry.percentage !== undefined ? "e.g., 60" : "e.g., 5000"}
                  />
                  {entry.amount !== undefined && entry.amount > 0 && (
                    <p className="text-xs text-cyan-500 dark:text-cyan-400 mt-1 font-medium">
                      = {formatTournamentMoney(entry.amount, currency)}
                    </p>
                  )}
                </div>

                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => toggleType(index, "percentage")}
                    className={`px-3 py-3 text-xs ${forcePercentageOnly ? "rounded" : "rounded-l"} font-medium ${
                      entry.percentage !== undefined
                        ? "bg-[rgba(169,78,206,1)] text-white"
                        : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                    }`}
                  >
                    %
                  </button>
                  {!forcePercentageOnly && (
                    <button
                      type="button"
                      onClick={() => toggleType(index, "amount")}
                      className={`px-3 py-3 text-xs rounded-r font-medium ${
                        entry.amount !== undefined
                          ? "bg-[rgba(169,78,206,1)] text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                      }`}
                    >
                      {getCurrencySymbol(currency)}
                    </button>
                  )}
                </div>
              </div>

              {entry.percentage !== undefined && prizePoolBase && parseFloat(prizePoolBase) > 0 && (
                <div className="flex-shrink-0 w-24 text-right">
                  <label className="text-xs text-gray-500 dark:text-gray-400">Amount</label>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {formatTournamentMoney((parseFloat(prizePoolBase) * entry.percentage) / 100, currency)}
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => removeEntry(index)}
                className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
              >
                <FaTrash />
              </button>
            </div>
          ))}

          {/* Summary */}
          {entries.length > 0 && (
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Total:</span>
                <div className="flex gap-4">
                  {hasPercentages && (
                    <span
                      className={`font-medium ${
                        totalPercentage === 100 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {totalPercentage.toFixed(2)}%{totalPercentage !== 100 && " (should be 100%)"}
                    </span>
                  )}
                  {hasAmounts && (
                    <span
                      className={`font-medium ${
                        !prizePoolBase || totalAmount <= parseFloat(prizePoolBase)
                          ? "text-green-600 dark:text-green-400"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {formatTournamentMoney(totalAmount, currency)}
                      {prizePoolBase && parseFloat(prizePoolBase) > 0 && totalAmount > parseFloat(prizePoolBase) && " (exceeds pool!)"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
